import { useMemo } from 'react'
import {
  CUSTOM_AUDIENCE_PRESET_NAME,
  getMarketPresetLanguages,
  useGetPresetAudiencesForLanguages
} from '../../../../hooks/audience/usePresetAudience'
import { useFieldwork } from '../../../../hooks/useFieldwork'
import { useIsAudienceReadOnly } from '../../../../hooks/useIsAudienceReadOnly'

/**
 * Whether the market's audience specification is locked because a weighted
 * preset audience is applied. Weighted presets automatically weight results
 * against their exact specification, so their criteria must not be editable
 * (the single-survey page enforces the same rule by disabling its "Edit
 * targeting and screening" button).
 *
 * Derived from the fieldwork's applied preset joined against the preset lists
 * of the market's languages — NOT from AudienceContext's isWeighted flag,
 * which is only dispatched by the preset picker while the Market settings
 * drawer is mounted (i.e. open) and would leave the page unlocked on a direct
 * load.
 *
 * Read-only markets short-circuit to unlocked without fetching: their page
 * renders the summary regardless, so resolving the preset would only delay
 * (or never affect) what the user sees.
 *
 * Fails closed, but distinguishes WHY so the page never claims "weighted"
 * before it knows:
 * - `resolving`: the preset lists are still loading (cold cache) — locked,
 *   and the caller should show a neutral loading state, since the applied
 *   preset may turn out unweighted;
 * - `unresolved`: the lists have settled but the applied preset wasn't found
 *   (fetch failure, or its locale was removed from the market) — locked,
 *   with copy saying the preset couldn't be verified rather than asserting
 *   weightedness.
 */
export const useWeightedPresetLock = (): {
  locked: boolean
  /** Locked only because the preset lists are still loading. */
  resolving?: boolean
  /** Locked because the applied preset wasn't found after loading settled. */
  unresolved?: boolean
  presetName?: string
} => {
  const { fieldwork } = useFieldwork()
  const { isReadOnly } = useIsAudienceReadOnly()
  const preset = fieldwork?.presetAudience
  // The single value every decision below hangs off: the applied preset when
  // it needs resolving (non-Custom on an editable market), else undefined.
  const presetToResolve =
    !isReadOnly && preset && preset.name !== CUSTOM_AUDIENCE_PRESET_NAME
      ? preset
      : undefined

  const languages = useMemo(
    () => getMarketPresetLanguages(fieldwork),
    [fieldwork]
  )

  // Nothing to resolve (custom/default markets, read-only markets) — an empty
  // country skips the fetch, so they short-circuit to unlocked with no flash
  // and no per-market network cost.
  const { data: presets, loading } = useGetPresetAudiencesForLanguages(
    presetToResolve ? fieldwork?.locale.country || '' : '',
    languages
  )

  if (!presetToResolve) {
    return { locked: false }
  }

  if (loading) {
    return { locked: true, resolving: true, presetName: presetToResolve.name }
  }

  const match = presets.find(
    ({ presetAudienceId }) =>
      presetAudienceId === presetToResolve.presetAudienceId
  )
  if (!match) {
    return { locked: true, unresolved: true, presetName: presetToResolve.name }
  }
  return { locked: !!match.isWeighted, presetName: presetToResolve.name }
}
